/**
 * main.tsx — SPA entry point: React Router + TanStack Query providers,
 * AuthProvider, and global styles.
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { App } from "./App";
import { AuthProvider } from "./context/AuthContext";
import { SiteProvider } from "./context/SiteContext";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Show what is already loaded instantly when you return to a page; refresh quietly in the background.
      staleTime: 15_000,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SiteProvider>
            <TooltipProvider delayDuration={300}>
              <App />
            </TooltipProvider>
          </SiteProvider>
          <Toaster
            position="bottom-right"
            closeButton
            visibleToasts={3}
            toastOptions={{
              duration: 5000,
              classNames: {
                toast: "!rounded-lg !border !border-neutral-200 !bg-white !px-4 !py-3 !text-sm !text-neutral-800 !shadow-lg",
                success: "[&_[data-icon]]:!text-success-600",
                error: "!border-danger-200 [&_[data-icon]]:!text-danger-600",
                warning: "!border-warning-200 [&_[data-icon]]:!text-warning-600",
                closeButton: "!border-neutral-200 !bg-white !text-neutral-500",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
